import { useEffect, useState } from 'react'
import { PortfolioNav, buildPortfolioProducts, type PortfolioNavLink } from '@saas-pages/shared/public/app-shell'
import { Seo } from '@saas-pages/shared/public/seo'
import branding from '../../branding.config'
import { IndexPage } from '@/pages/index/IndexPage'
import { OnlinePaintPage } from '@/pages/online-paint'
import { BlogIndexPage } from '@/pages/blog/BlogIndexPage'
import { BlogPostPage } from '@/pages/blog/BlogPostPage'
import { AvifConverterPage } from '@/pages/avif-converter/AvifConverterPage'
import { BlurImagePage } from '@/pages/blur-image/BlurImagePage'
import { CompressImagePage } from '@/pages/compress-image/CompressImagePage'
import { CropImagePage } from '@/pages/crop-image/CropImagePage'
import { Photo3x4Page } from '@/pages/photo-3x4/Photo3x4Page'
import { PhotoCollagePage } from '@/pages/photo-collage/PhotoCollagePage'
import { ImageGifMakerPage } from '@/pages/image-gif-maker/ImageGifMakerPage'
import { RemoveExifPage } from '@/pages/remove-exif/RemoveExifPage'
import { MetadataViewerPage } from '@/pages/metadata-viewer/MetadataViewerPage'
import { ResizeImagePage } from '@/pages/resize-image/ResizeImagePage'
import { SocialMediaResizePage } from '@/pages/social-media-resize/SocialMediaResizePage'
import { PhysicalPhotoSizePage } from '@/pages/physical-photo-size/PhysicalPhotoSizePage'
import { RotateImagePage } from '@/pages/rotate-image/RotateImagePage'
import { SvgConverterPage } from '@/pages/svg-converter/SvgConverterPage'
import { UpscaleImagePage } from '@/pages/upscale-image/UpscaleImagePage'
import { WatermarkImagePage } from '@/pages/watermark-image/WatermarkImagePage'
import { TiffToJpgPage } from '@/pages/tiff-to-jpg/TiffToJpgPage'
import { ConverterPage } from '@/pages/png-jpg-converter/ConverterPage'
import {
  resolveConverterLanding,
  type ConverterLanding,
} from '@/pages/png-jpg-converter/model/converter'
import { HeicToJpgPage } from '@/pages/heic-to-jpg/HeicToJpgPage'
import { HeicViewerPage } from '@/pages/heic-viewer/HeicViewerPage'
import { HtmlToImagePage } from '@/pages/html-to-image/HtmlToImagePage'
import { ImageToWebpPage } from '@/pages/image-to-webp/ImageToWebpPage'
import { ImageColorPickerPage } from '@/pages/image-color-picker'
import { JpgViewerPage } from '@/pages/jpg-viewer/JpgViewerPage'
import { PngViewerPage } from '@/pages/png-viewer/PngViewerPage'
import { WebpViewerPage } from '@/pages/webp-viewer/WebpViewerPage'
import { SvgViewerPage } from '@/pages/svg-viewer/SvgViewerPage'
import { AvifViewerPage } from '@/pages/avif-viewer/AvifViewerPage'
import { TiffViewerPage } from '@/pages/tiff-viewer/TiffViewerPage'
import { GifViewerPage } from '@/pages/gif-viewer/GifViewerPage'
import { BmpViewerPage } from '@/pages/bmp-viewer/BmpViewerPage'
import { IcoViewerPage } from '@/pages/ico-viewer/IcoViewerPage'
import { WebpConverterPage } from '@/pages/webp-converter/WebpConverterPage'
import { FaviconGeneratorPage } from '@/pages/favicon-generator/FaviconGeneratorPage'
import { seoFor } from '@/seo/routes'
import { useLocale, useT } from '@/shared/i18n'
import { ThemeSwitcher } from '@/shared/theme/theme'
import { LangSwitcher } from '@/widgets/lang-switcher/LangSwitcher'

interface AppProps {
  pathname?: string
}

export function App({ pathname }: AppProps = {}) {
  const t = useT()
  const { locale } = useLocale()
  const routeState = currentRouteState(pathname)
  const seoKey = routeState.key === 'blogPost' ? 'blog' : routeState.key
  const route = seoFor(seoKey, locale)
  const navItems: PortfolioNavLink[] = [
    {
      key: 'online-paint',
      label: t.nav.onlinePaint,
      href: '/risovat-online',
      current: routeState.key === 'onlinePaint',
    },
    {
      key: 'image-color-picker',
      label: t.nav.imageColorPicker,
      href: '/vybrat-cvet-s-kartinki',
      current: routeState.key === 'imageColorPicker',
    },
    {
      key: 'png-jpg',
      label: t.nav.pngJpg,
      href: '/konverter-izobrazheniy',
      current: routeState.key === 'imageConverter',
    },
    {
      key: 'crop-image',
      label: t.nav.cropImage,
      href: '/obrezat-foto',
      current: routeState.key === 'cropImage',
    },
    {
      key: 'rotate-image',
      label: t.nav.rotateImage,
      href: '/povernut-foto',
      current: routeState.key === 'rotateImage',
    },
    {
      key: 'blur-image',
      label: t.nav.blurImage,
      href: '/razmyt-foto',
      current: routeState.key === 'blurImage',
    },
    {
      key: 'watermark-image',
      label: t.nav.watermarkImage,
      href: '/vodyanoj-znak-na-foto',
      current: routeState.key === 'watermarkImage',
    },
    {
      key: 'photo-3x4',
      label: t.nav.photo3x4,
      href: '/foto-3x4',
      current: routeState.key === 'photo3x4',
    },
    {
      key: 'photo-collage',
      label: t.nav.photoCollage,
      href: '/kollazh-iz-foto',
      current: routeState.key === 'photoCollage',
    },
    {
      key: 'image-gif-maker',
      label: t.nav.imageGifMaker,
      href: '/sozdat-gif-iz-foto',
      current: routeState.key === 'imageGifMaker',
    },
    {
      key: 'compress-image',
      label: t.nav.compressImage,
      href: '/szhat-izobrazhenie',
      current: routeState.key === 'compressImage',
    },
    {
      key: 'remove-exif',
      label: t.nav.removeExif,
      href: '/udalit-exif',
      current: routeState.key === 'removeExif',
    },
    {
      key: 'metadata-viewer',
      label: t.nav.metadataViewer,
      href: '/posmotret-exif',
      current: routeState.key === 'metadataViewer',
    },
    {
      key: 'physical-photo-size',
      label: t.nav.physicalPhotoSize,
      href: '/razmer-foto-v-sm',
      current: routeState.key === 'physicalPhotoSize',
    },
    {
      key: 'image-webp',
      label: t.nav.imageWebp,
      href: '/v-webp',
      current: routeState.key === 'imageToWebp',
    },
    {
      key: 'webp-converter',
      label: t.nav.webpConverter,
      href: '/webp-v-jpg-png',
      current: routeState.key === 'webpConverter',
    },
    {
      key: 'avif-converter',
      label: t.nav.avifConverter,
      href: '/avif-konverter',
      current: routeState.key === 'avifConverter',
    },
    {
      key: 'tiff-jpg',
      label: t.nav.tiffJpg,
      href: '/tiff-v-jpg',
      current: routeState.key === 'tiffToJpg',
    },
    {
      key: 'svg-converter',
      label: t.nav.svgConverter,
      href: '/svg-v-png-jpg',
      current: routeState.key === 'svgConverter',
    },
    {
      key: 'html-to-image',
      label: t.nav.htmlToImage,
      href: '/html-v-kartinku',
      current: routeState.key === 'htmlToImage',
    },
    {
      key: 'resize-image',
      label: t.nav.resizeImage,
      href: '/izmenit-razmer-foto',
      current: routeState.key === 'resizeImage',
    },
    {
      key: 'social-media-resize',
      label: t.nav.socialMediaResize,
      href: '/razmer-foto-dlya-socsetey',
      current: routeState.key === 'socialMediaResize',
    },
    {
      key: 'upscale-image',
      label: t.nav.upscaleImage,
      href: '/uvelichit-foto',
      current: routeState.key === 'upscaleImage',
    },
    {
      key: 'favicon-generator',
      label: t.nav.faviconGenerator,
      href: '/favicon-generator',
      current: routeState.key === 'faviconGenerator',
    },
    {
      key: 'heic-viewer',
      label: t.nav.heicViewer,
      href: '/otkryt-heic',
      current: routeState.key === 'heicViewer',
    },
    {
      key: 'jpg-viewer',
      label: t.nav.jpgViewer,
      href: '/otkryt-jpg',
      current: routeState.key === 'jpgViewer',
    },
    {
      key: 'png-viewer',
      label: t.nav.pngViewer,
      href: '/otkryt-png',
      current: routeState.key === 'pngViewer',
    },
    {
      key: 'webp-viewer',
      label: t.nav.webpViewer,
      href: '/otkryt-webp',
      current: routeState.key === 'webpViewer',
    },
    {
      key: 'svg-viewer',
      label: t.nav.svgViewer,
      href: '/otkryt-svg',
      current: routeState.key === 'svgViewer',
    },
    {
      key: 'avif-viewer',
      label: t.nav.avifViewer,
      href: '/otkryt-avif',
      current: routeState.key === 'avifViewer',
    },
    {
      key: 'tiff-viewer',
      label: t.nav.tiffViewer,
      href: '/otkryt-tiff',
      current: routeState.key === 'tiffViewer',
    },
    {
      key: 'gif-viewer',
      label: t.nav.gifViewer,
      href: '/otkryt-gif',
      current: routeState.key === 'gifViewer',
    },
    {
      key: 'bmp-viewer',
      label: t.nav.bmpViewer,
      href: '/otkryt-bmp',
      current: routeState.key === 'bmpViewer',
    },
    {
      key: 'ico-viewer',
      label: t.nav.icoViewer,
      href: '/otkryt-ico',
      current: routeState.key === 'icoViewer',
    },
    {
      key: 'heic-jpg',
      label: t.nav.heicJpg,
      href: '/heic-v-jpg',
      current: routeState.key === 'heicToJpg',
    },
    {
      key: 'blog',
      label: t.nav.blog,
      href: '/blog',
      current: routeState.key === 'blog' || routeState.key === 'blogPost',
    },
  ]

  return (
    <div className="min-h-screen bg-background text-foreground">
      {routeState.key !== 'blogPost' && <Seo {...route} ogImage={branding.ogImage} />}
      <PortfolioNav
        brandLabel="imgify.ru"
        brandHref="/"
        products={buildPortfolioProducts('imgify')}
        items={navItems}
        sourceToolKey={sourceToolKeyForImgify(routeState)}
        actions={(
          <>
            <ThemeSwitcher />
            <LangSwitcher />
          </>
        )}
      />

      <main className="px-4 py-3 sm:py-10">
        {renderRoute(routeState)}
      </main>
    </div>
  )
}

type RouteState =
  | { key: 'index' }
  | { key: 'onlinePaint' }
  | { key: 'imageConverter' }
  | { key: 'imageColorPicker' }
  | { key: 'cropImage' }
  | { key: 'rotateImage' }
  | { key: 'blurImage' }
  | { key: 'watermarkImage' }
  | { key: 'photo3x4' }
  | { key: 'photoCollage' }
  | { key: 'imageGifMaker' }
  | { key: 'compressImage' }
  | { key: 'removeExif' }
  | { key: 'physicalPhotoSize' }
  | { key: 'metadataViewer' }
  | { key: 'imageToWebp' }
  | { key: 'webpConverter' }
  | { key: 'avifConverter' }
  | { key: 'tiffToJpg' }
  | { key: 'svgConverter' }
  | { key: 'htmlToImage' }
  | { key: 'resizeImage' }
  | { key: 'socialMediaResize' }
  | { key: 'upscaleImage' }
  | { key: 'faviconGenerator' }
  | { key: 'heicViewer' }
  | { key: 'jpgViewer' }
  | { key: 'pngViewer' }
  | { key: 'webpViewer' }
  | { key: 'svgViewer' }
  | { key: 'avifViewer' }
  | { key: 'tiffViewer' }
  | { key: 'gifViewer' }
  | { key: 'bmpViewer' }
  | { key: 'icoViewer' }
  | { key: 'heicToJpg' }
  | { key: 'blog' }
  | { key: 'blogPost'; slug: string }

type ImgifyToolRouteKey = Exclude<RouteState['key'], 'index' | 'blog' | 'blogPost'>

const IMGIFY_SOURCE_TOOL_KEYS = {
  onlinePaint: 'online-paint',
  imageConverter: 'image-converter',
  imageColorPicker: 'image-color-picker',
  cropImage: 'crop-image',
  rotateImage: 'rotate-image',
  blurImage: 'blur-image',
  watermarkImage: 'watermark-image',
  photo3x4: 'photo-3x4',
  photoCollage: 'photo-collage',
  imageGifMaker: 'image-gif-maker',
  compressImage: 'compress-image',
  removeExif: 'remove-exif',
  metadataViewer: 'metadata-viewer',
  imageToWebp: 'image-to-webp',
  webpConverter: 'webp-converter',
  avifConverter: 'avif-converter',
  tiffToJpg: 'tiff-to-jpg',
  physicalPhotoSize: 'physical-photo-size',
  svgConverter: 'svg-converter',
  htmlToImage: 'html-to-image',
  resizeImage: 'resize-image',
  socialMediaResize: 'social-media-resize',
  upscaleImage: 'upscale-image',
  faviconGenerator: 'favicon-generator',
  heicViewer: 'heic-viewer',
  jpgViewer: 'jpg-viewer',
  pngViewer: 'png-viewer',
  webpViewer: 'webp-viewer',
  svgViewer: 'svg-viewer',
  avifViewer: 'avif-viewer',
  tiffViewer: 'tiff-viewer',
  gifViewer: 'gif-viewer',
  bmpViewer: 'bmp-viewer',
  icoViewer: 'ico-viewer',
  heicToJpg: 'heic-to-jpg',
} as const satisfies Record<ImgifyToolRouteKey, string>

function sourceToolKeyForImgify(routeState: RouteState): string | undefined {
  if (routeState.key === 'index' || routeState.key === 'blog' || routeState.key === 'blogPost') {
    return undefined
  }

  return IMGIFY_SOURCE_TOOL_KEYS[routeState.key]
}

function currentRouteState(staticPathname?: string): RouteState {
  if (!staticPathname && typeof window === 'undefined') {
    return { key: 'index' }
  }
  const pathname = (staticPathname ?? window.location.pathname).replace(/\/+$/, '') || '/'

  if (pathname === '/risovat-online') {
    return { key: 'onlinePaint' }
  }
  if (pathname === '/konverter-izobrazheniy') {
    return { key: 'imageConverter' }
  }
  if (pathname === '/vybrat-cvet-s-kartinki') {
    return { key: 'imageColorPicker' }
  }
  if (pathname === '/obrezat-foto') {
    return { key: 'cropImage' }
  }
  if (pathname === '/povernut-foto') {
    return { key: 'rotateImage' }
  }
  if (pathname === '/razmyt-foto') {
    return { key: 'blurImage' }
  }
  if (pathname === '/vodyanoj-znak-na-foto') {
    return { key: 'watermarkImage' }
  }
  if (pathname === '/foto-3x4') {
    return { key: 'photo3x4' }
  }
  if (pathname === '/kollazh-iz-foto') {
    return { key: 'photoCollage' }
  }
  if (pathname === '/sozdat-gif-iz-foto') {
    return { key: 'imageGifMaker' }
  }
  if (pathname === '/szhat-izobrazhenie') {
    return { key: 'compressImage' }
  }
  if (pathname === '/udalit-exif') {
    return { key: 'removeExif' }
  }
  if (pathname === '/posmotret-exif') {
    return { key: 'metadataViewer' }
  }
  if (pathname === '/razmer-foto-v-sm') {
    return { key: 'physicalPhotoSize' }
  }
  if (pathname === '/v-webp') {
    return { key: 'imageToWebp' }
  }
  if (pathname === '/webp-v-jpg-png') {
    return { key: 'webpConverter' }
  }
  if (pathname === '/avif-konverter') {
    return { key: 'avifConverter' }
  }
  if (pathname === '/tiff-v-jpg') {
    return { key: 'tiffToJpg' }
  }
  if (pathname === '/svg-v-png-jpg') {
    return { key: 'svgConverter' }
  }
  if (pathname === '/html-v-kartinku') {
    return { key: 'htmlToImage' }
  }
  if (pathname === '/izmenit-razmer-foto') {
    return { key: 'resizeImage' }
  }
  if (pathname === '/razmer-foto-dlya-socsetey') {
    return { key: 'socialMediaResize' }
  }
  if (pathname === '/uvelichit-foto') {
    return { key: 'upscaleImage' }
  }
  if (pathname === '/favicon-generator') {
    return { key: 'faviconGenerator' }
  }
  if (pathname === '/otkryt-heic') {
    return { key: 'heicViewer' }
  }
  if (pathname === '/otkryt-jpg') {
    return { key: 'jpgViewer' }
  }
  if (pathname === '/otkryt-png') {
    return { key: 'pngViewer' }
  }
  if (pathname === '/otkryt-webp') {
    return { key: 'webpViewer' }
  }
  if (pathname === '/otkryt-svg') {
    return { key: 'svgViewer' }
  }
  if (pathname === '/otkryt-avif') {
    return { key: 'avifViewer' }
  }
  if (pathname === '/otkryt-tiff') {
    return { key: 'tiffViewer' }
  }
  if (pathname === '/otkryt-gif') {
    return { key: 'gifViewer' }
  }
  if (pathname === '/otkryt-bmp') {
    return { key: 'bmpViewer' }
  }
  if (pathname === '/otkryt-ico') {
    return { key: 'icoViewer' }
  }
  if (pathname === '/heic-v-jpg') {
    return { key: 'heicToJpg' }
  }
  if (pathname === '/blog') {
    return { key: 'blog' }
  }
  if (pathname.startsWith('/blog/')) {
    return { key: 'blogPost', slug: decodeURIComponent(pathname.slice('/blog/'.length)) }
  }

  return { key: 'index' }
}

function renderRoute(routeState: RouteState) {
  switch (routeState.key) {
    case 'onlinePaint':
      return <OnlinePaintPage />
    case 'imageColorPicker':
      return <ImageColorPickerPage />
    case 'imageConverter':
      return <ImageConverterRoute />
    case 'cropImage':
      return <CropImagePage />
    case 'rotateImage':
      return <RotateImagePage />
    case 'blurImage':
      return <BlurImagePage />
    case 'watermarkImage':
      return <WatermarkImagePage />
    case 'photo3x4':
      return <Photo3x4Page />
    case 'photoCollage':
      return <PhotoCollagePage />
    case 'imageGifMaker':
      return <ImageGifMakerPage />
    case 'compressImage':
      return <CompressImagePage />
    case 'removeExif':
      return <RemoveExifPage />
    case 'metadataViewer':
      return <MetadataViewerPage />
    case 'physicalPhotoSize':
      return <PhysicalPhotoSizePage />
    case 'imageToWebp':
      return <ImageToWebpPage />
    case 'webpConverter':
      return <WebpConverterPage />
    case 'avifConverter':
      return <AvifConverterPage />
    case 'tiffToJpg':
      return <TiffToJpgPage />
    case 'svgConverter':
      return <SvgConverterPage />
    case 'htmlToImage':
      return <HtmlToImagePage />
    case 'resizeImage':
      return <ResizeImagePage />
    case 'socialMediaResize':
      return <SocialMediaResizePage />
    case 'upscaleImage':
      return <UpscaleImagePage />
    case 'faviconGenerator':
      return <FaviconGeneratorPage />
    case 'heicViewer':
      return <HeicViewerPage />
    case 'jpgViewer':
      return <JpgViewerPage />
    case 'pngViewer':
      return <PngViewerPage />
    case 'webpViewer':
      return <WebpViewerPage />
    case 'svgViewer':
      return <SvgViewerPage />
    case 'avifViewer':
      return <AvifViewerPage />
    case 'tiffViewer':
      return <TiffViewerPage />
    case 'gifViewer':
      return <GifViewerPage />
    case 'bmpViewer':
      return <BmpViewerPage />
    case 'icoViewer':
      return <IcoViewerPage />
    case 'heicToJpg':
      return <HeicToJpgPage />
    case 'blog':
      return <BlogIndexPage />
    case 'blogPost':
      return <BlogPostPage slug={routeState.slug} />
    case 'index':
    default:
      return <IndexPage />
  }
}

const CANONICAL_CONVERTER_LANDING: ConverterLanding = {
  direction: 'pngToJpg',
  source: 'canonical',
}

interface ImageConverterRouteProps {
  search?: string
}

export function ImageConverterRoute({ search }: ImageConverterRouteProps = {}) {
  const [landing, setLanding] = useState<ConverterLanding>(CANONICAL_CONVERTER_LANDING)

  useEffect(() => {
    const resolved = resolveConverterLanding(search ?? window.location.search)
    if (
      resolved.direction !== CANONICAL_CONVERTER_LANDING.direction
      || resolved.source !== CANONICAL_CONVERTER_LANDING.source
    ) {
      setLanding(resolved)
    }
  }, [search])

  return (
    <ConverterPage
      key={`${landing.direction}:${landing.source}`}
      initialDirection={landing.direction}
      landingSource={landing.source}
    />
  )
}
